/**
 * Silent calendar Event create/helpers (calendar_tasks).
 * Used by the assistant time-planning flow — no Create Event form.
 */
import { supabase } from './supabase';
import { triggerGcalSync } from './bookings';
import { completeTodoItem, getTodoItem, updateTodoItem } from './todos';

export interface CreateCalendarEventInput {
  title: string;
  notes?: string | null;
  start_date: string;
  start_time: string;
  end_date?: string;
  end_time?: string;
  /** Duration in hours — used when end_* omitted */
  duration_hours?: number;
  customer_id?: string | null;
  resource_ids?: string[];
  todo_item_id?: string | null;
  is_mobile?: boolean;
  task_address?: string | null;
}

export interface CalendarEventRow {
  id: string;
  business_id: string;
  title: string;
  notes: string | null;
  status: string;
  customer_id: string | null;
  start_date: string;
  start_time: string;
  end_date: string;
  end_time: string;
  todo_item_id: string | null;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
}

function requireSupabase() {
  if (!supabase) throw new Error('Supabase client is not initialized');
  return supabase;
}

function normalizeTime(time: string): string {
  const t = time.trim();
  if (/^\d{2}:\d{2}$/.test(t)) return `${t}:00`;
  if (/^\d{2}:\d{2}:\d{2}$/.test(t)) return t;
  throw new Error('time must be HH:MM or HH:MM:SS');
}

function addMinutesToTime(
  date: string,
  time: string,
  minutes: number
): { date: string; time: string } {
  const [hh, mm] = normalizeTime(time).slice(0, 5).split(':').map(Number);
  const start = new Date(`${date}T00:00:00`);
  start.setHours(hh, mm, 0, 0);
  start.setMinutes(start.getMinutes() + minutes);
  const y = start.getFullYear();
  const m = String(start.getMonth() + 1).padStart(2, '0');
  const d = String(start.getDate()).padStart(2, '0');
  const th = String(start.getHours()).padStart(2, '0');
  const tm = String(start.getMinutes()).padStart(2, '0');
  return { date: `${y}-${m}-${d}`, time: `${th}:${tm}:00` };
}

export function refreshCalendarAfterEvent(businessId: string, taskId: string): void {
  window.dispatchEvent(new CustomEvent('taskCreated', { detail: { taskId, businessId } }));
}

export async function createCalendarEvent(
  businessId: string,
  input: CreateCalendarEventInput
): Promise<CalendarEventRow> {
  const client = requireSupabase();
  const title = String(input.title || '').trim();
  if (!title) throw new Error('Title is required');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(input.start_date)) {
    throw new Error('start_date must be YYYY-MM-DD');
  }

  const startTime = normalizeTime(input.start_time || '09:00');
  let endDate = input.end_date || input.start_date;
  let endTime: string;

  if (input.end_time) {
    endTime = normalizeTime(input.end_time);
    if (input.end_date) endDate = input.end_date;
  } else {
    const durationHours = Number(input.duration_hours) || 1;
    const mins = Math.round(durationHours * 60);
    if (mins <= 0 || mins > 14 * 24 * 60) {
      throw new Error('duration_hours must be a positive number of hours');
    }
    const end = addMinutesToTime(input.start_date, startTime, mins);
    endDate = end.date;
    endTime = end.time;
  }

  const startDt = new Date(`${input.start_date}T${startTime}`);
  const endDt = new Date(`${endDate}T${endTime}`);
  if (endDt <= startDt) {
    throw new Error('End date/time must be after start');
  }

  let todoItemId: string | null = input.todo_item_id ? String(input.todo_item_id).trim() : null;
  let customerId = input.customer_id ?? null;
  const resourceIds = [...(input.resource_ids || [])];

  if (todoItemId) {
    const todo = await getTodoItem(businessId, todoItemId);
    if (!todo) throw new Error('Linked Task not found');
    if (!customerId && todo.customer_id) customerId = todo.customer_id;
    if (resourceIds.length === 0 && todo.assignee_resource_id) {
      resourceIds.push(todo.assignee_resource_id);
    }
  }

  const { data, error } = await client
    .from('calendar_tasks')
    .insert({
      business_id: businessId,
      title,
      notes: input.notes?.trim() || null,
      status: 'scheduled',
      customer_id: customerId || null,
      start_date: input.start_date,
      start_time: startTime,
      end_date: endDate,
      end_time: endTime,
      is_mobile: input.is_mobile === true,
      task_address: input.is_mobile ? input.task_address?.trim() || null : null,
      todo_item_id: todoItemId,
    })
    .select()
    .single();

  if (error) throw new Error(error.message || 'Failed to create event');

  if (resourceIds.length > 0) {
    const { error: resError } = await client.from('calendar_task_resources').insert(
      resourceIds.map((resource_id) => ({
        task_id: data.id,
        resource_id,
      }))
    );
    if (resError) {
      console.error('Failed to assign resources to event:', resError);
    }
  }

  refreshCalendarAfterEvent(businessId, data.id);
  return data as CalendarEventRow;
}

/** Find upcoming/non-deleted Events linked to a todo item. */
export async function findEventsForTodo(
  businessId: string,
  todoItemId: string
): Promise<CalendarEventRow[]> {
  const client = requireSupabase();
  const { data, error } = await client
    .from('calendar_tasks')
    .select('*')
    .eq('business_id', businessId)
    .eq('todo_item_id', todoItemId)
    .eq('is_deleted', false)
    .order('start_date', { ascending: true })
    .order('start_time', { ascending: true })
    .limit(20);
  if (error) throw new Error(error.message);
  return (data || []) as CalendarEventRow[];
}

/** Find linked Events for many todos (for task list UI). */
export async function findEventsForTodos(
  businessId: string,
  todoItemIds: string[]
): Promise<Map<string, CalendarEventRow>> {
  const map = new Map<string, CalendarEventRow>();
  const ids = [...new Set(todoItemIds.filter(Boolean))];
  if (ids.length === 0) return map;
  const client = requireSupabase();
  const { data, error } = await client
    .from('calendar_tasks')
    .select('*')
    .eq('business_id', businessId)
    .in('todo_item_id', ids)
    .eq('is_deleted', false)
    .order('start_date', { ascending: true })
    .order('start_time', { ascending: true });
  if (error) throw new Error(error.message);
  for (const row of (data || []) as CalendarEventRow[]) {
    if (!row.todo_item_id) continue;
    // Keep earliest scheduled event per todo
    if (!map.has(row.todo_item_id)) map.set(row.todo_item_id, row);
  }
  return map;
}

export async function getTodoLinkedFromEvent(
  businessId: string,
  todoItemId: string
): Promise<{ id: string; title: string } | null> {
  const todo = await getTodoItem(businessId, todoItemId);
  if (!todo) return null;
  return { id: todo.id, title: todo.title };
}

/** When a linked calendar Event is marked completed, complete the Control Centre Task too. */
export async function syncLinkedTodoOnEventCompleted(
  businessId: string,
  opts: { todoItemId?: string | null; calendarTaskId?: string }
): Promise<void> {
  let todoItemId = String(opts.todoItemId || '').trim() || null;

  if (!todoItemId && opts.calendarTaskId) {
    const client = requireSupabase();
    const { data, error } = await client
      .from('calendar_tasks')
      .select('todo_item_id')
      .eq('id', opts.calendarTaskId)
      .eq('business_id', businessId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    todoItemId = data?.todo_item_id ? String(data.todo_item_id) : null;
  }

  if (!todoItemId) return;

  const todo = await getTodoItem(businessId, todoItemId);
  if (!todo || todo.status === 'completed') return;

  await completeTodoItem(businessId, todoItemId);
  window.dispatchEvent(
    new CustomEvent('todoItemsChanged', { detail: { businessId } })
  );
}

/** When a Control Centre Task is marked completed, complete linked calendar Events too. */
export async function syncLinkedEventOnTodoCompleted(
  businessId: string,
  todoItemId: string
): Promise<void> {
  const id = String(todoItemId || '').trim();
  if (!id) return;

  const client = requireSupabase();
  const { data: events, error } = await client
    .from('calendar_tasks')
    .select('id, status')
    .eq('business_id', businessId)
    .eq('todo_item_id', id)
    .eq('is_deleted', false)
    .neq('status', 'completed');

  if (error) throw new Error(error.message);
  if (!events?.length) return;

  const completedAt = new Date().toISOString();
  for (const event of events) {
    const { error: updateErr } = await client
      .from('calendar_tasks')
      .update({ status: 'completed', completed_at: completedAt })
      .eq('id', event.id);
    if (updateErr) throw new Error(updateErr.message);
    triggerGcalSync(event.id, 'update', businessId, 'task');
    refreshCalendarAfterEvent(businessId, event.id);
  }
}

/** Persist duration on the Task after the user answers. */
export async function setTodoEstimatedMinutes(
  businessId: string,
  todoId: string,
  minutes: number
): Promise<void> {
  await updateTodoItem(businessId, todoId, { estimated_minutes: minutes });
}
